// src/services/extDealService.ts
import { AxiosResponse, AxiosRequestConfig } from 'axios';
import { hmacJson } from './hmacAxios.ts';

// Permite headers extra + Idempotency-Key opcional
type IdempotentExtra = Omit<AxiosRequestConfig, 'headers'> & {
  idempotencyKey?: string;
  headers?: AxiosRequestConfig['headers'];
};

function withIdem(extra?: IdempotentExtra): AxiosRequestConfig | undefined {
  if (!extra) return undefined;
  const { idempotencyKey, headers, ...rest } = extra;
  return {
    ...rest,
    headers: {
      ...(headers || {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
  };
}

// Note: dealPublicId should NOT be sent as query parameter

class ExtDealService {
  // ===================== EXISTENTES (resumen) =====================
  public getDealByPrivateIdExt(
    dealPrivateId: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'GET',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}`,
      undefined,
      undefined,
      withIdem(extra)
    );
  }

  public getSelectionStatusExt(
    dealPrivateId: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'GET',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/selection-status`,
      undefined,
      undefined,
      withIdem(extra)
    );
  }

  public createDealAddendumExt(
    payload: any,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'POST',
      `/deal-service/ext/deal-addendums`,
      payload,
      undefined,
      withIdem(extra)
    );
  }

  public updateDealStatusExt(
    dealPrivateId: string,
    toStatus: string,
    details?: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    const body = details ? { toStatus, details } : { toStatus };
    return hmacJson(
      'POST',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/status`,
      body,
      undefined,
      withIdem(extra)
    );
  }

  public updateDealSubstatusExt(
    dealPrivateId: string,
    substatus: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'PATCH',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/substatus`,
      { substatus },
      undefined,
      withIdem(extra)
    );
  }

  public updateHasBlankCheckExt(
    dealPrivateId: string,
    hasBlankCheck: boolean,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'PATCH',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/has-blank-check`,
      { hasBlankCheck },
      undefined,
      withIdem(extra)
    );
  }

  /**
   * Obtener template por action (equiv. a getFundingDocumentTemplate, pero general)
   * GET /template-service/ext/templates/{action}
   */
  public getTemplateByActionExt(
    action: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'GET',
      `/template-service/ext/templates/${encodeURIComponent(action)}`,
      undefined,
      undefined,
      withIdem(extra)
    );
  }

  /**
   * Obtener una oferta específica de un deal (externo)
   * GET /deal-service/ext/deals/{dealPrivateId}/offers/{offerId}
   */
  public getDealOfferByIdExt(
    dealPrivateId: string,
    offerId: string,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'GET',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/offers/${encodeURIComponent(offerId)}`,
      undefined,
      undefined,
      withIdem(extra)
    );
  }

  /**
   * Despachar comunicaciones (externo)
   * POST /communication-service/ext/communications/dispatch
   */
  public dispatchCommunicationExt(
    payload: {
      requestId: string;
      action: string;
      contacts: any[];
      templates: any[];
    },
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'POST',
      `/communication-service/ext/communications/dispatch`,
      payload,
      undefined,
      withIdem(extra)
    );
  }

  // (Opcional) Si más adelante necesitas el flujo addendum+contract externo:
  // POST /communication-service/ext/communications/addendum-contract/dispatch
  public dispatchAddendumContractWorkflowExt?(
    payload: any,
    extra?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'POST',
      `/communication-service/ext/communications/addendum-contract/dispatch`,
      payload,
      undefined,
      withIdem(extra)
    );
  }

  // ===================== NUEVOS (Offers externos) =====================

  /**
   * Crear oferta externa
   * POST /deal-service/ext/deals/{dealPrivateId}/offers
   *
   * - `offer` es el OfferDTO.
   * - `options.idempotencyKey` (opcional) va como header.
   */
  public createDealOfferExt(
    dealPrivateId: string,
    offer: any,
    options?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'POST',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/offers`,
      offer,
      undefined,
      withIdem(options)
    );
  }

  /**
   * Actualizar oferta externa
   * PUT /deal-service/ext/deals/{dealPrivateId}/offers/{offerId}
   *
   * - `offer` es el OfferDTO (body).
   * - `options.idempotencyKey` (opcional) va como header.
   */
  public updateDealOfferExt(
    dealPrivateId: string,
    offerId: string,
    offer: any,
    options?: IdempotentExtra
  ): Promise<AxiosResponse<any>> {
    return hmacJson(
      'PUT',
      `/deal-service/ext/deals/${encodeURIComponent(dealPrivateId)}/offers/${encodeURIComponent(offerId)}`,
      offer,
      undefined,
      withIdem(options)
    );
  }
}

export default new ExtDealService();